import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ROUTE_PATHS = {
  home: "/",
  about: "/tentang",
  services: "/layanan",
  products: "/produk",
  articles: "/artikel",
  gallery: "/galeri",
  faq: "/faq",
  contact: "/kontak",
};

const AdminPages = () => {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");

  const load = () => api.get("/pages").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const systemPages = items.filter((p) => p.is_system);
  const customPages = items.filter((p) => !p.is_system);

  const create = async () => {
    if (!title.trim()) return;
    try {
      const { data } = await api.post("/pages", { title, sections: [], published: false });
      toast.success("Halaman dibuat");
      setTitle(""); setCreating(false); load();
      window.location.href = `/admin/pages/${data.id}`;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus halaman ini?")) return;
    try {
      await api.delete(`/pages/${id}`);
      toast.success("Terhapus");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Gagal menghapus");
    }
  };

  const publicHref = (p) => p.public_path || (p.route_key ? ROUTE_PATHS[p.route_key] : `/p/${p.slug}`) || `/p/${p.slug}`;

  const Table = ({ rows, allowDelete }) => (
    <div className="bg-white rounded-2xl border border-zinc-200 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-left text-xs uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="p-4">Judul</th>
            <th className="p-4">URL</th>
            <th className="p-4">Sections</th>
            <th className="p-4">Status</th>
            <th className="p-4"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => (
            <tr key={p.id} className="border-t border-zinc-100">
              <td className="p-4 font-medium text-zinc-900">
                {p.title}
                {p.is_system && <span className="ml-2 text-[10px] uppercase tracking-wider text-maroon-700 bg-maroon-50 px-2 py-0.5 rounded-full">Sistem</span>}
              </td>
              <td className="p-4 text-zinc-500 text-xs font-mono">{publicHref(p)}</td>
              <td className="p-4 text-zinc-600">{(p.sections || []).length} section</td>
              <td className="p-4">{p.published ? <span className="text-maroon-700 font-semibold">Publish</span> : <span className="text-zinc-400">Draft</span>}</td>
              <td className="p-4 text-right whitespace-nowrap">
                {p.published && (
                  <a href={publicHref(p)} target="_blank" rel="noreferrer" className="inline-flex p-2 rounded-lg hover:bg-zinc-100" title="Buka di situs">
                    <ExternalLink size={14} />
                  </a>
                )}
                <Link to={`/admin/pages/${p.id}`} data-testid={`edit-page-${p.id}`} className="inline-flex p-2 rounded-lg hover:bg-zinc-100"><Pencil size={14} /></Link>
                {allowDelete && (
                  <button onClick={() => del(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-600"><Trash2 size={14} /></button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={5} className="p-10 text-center text-zinc-500">Belum ada halaman.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-display font-bold">Pages (Page Builder)</h1>
          <p className="mt-1 text-sm text-zinc-500">
            Edit semua halaman menu situs (teks, gambar, susunan section). Upload gambar dari lokal. Halaman kustom tetap di <code className="text-maroon-700">/p/&#123;slug&#125;</code>.
          </p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-page" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-maroon-800 text-white font-semibold hover:bg-maroon-900">
          <Plus size={16} /> Halaman Baru
        </button>
      </div>

      <h2 className="text-lg font-display font-bold text-zinc-900 mb-3">Halaman Situs</h2>
      <Table rows={systemPages} allowDelete={false} />

      <h2 className="text-lg font-display font-bold text-zinc-900 mt-10 mb-3">Halaman Kustom</h2>
      <Table rows={customPages} allowDelete />

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md p-6">
            <h3 className="text-xl font-display font-bold mb-4">Halaman Baru</h3>
            <label className="block text-sm font-semibold mb-2">Judul Halaman</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="new-page-title"
              placeholder="Misal: Landing Sale Ramadhan"
              className="w-full px-4 py-3 rounded-xl border border-zinc-200 focus:border-maroon-700 outline-none" autoFocus />
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setCreating(false); setTitle(""); }} className="px-5 py-2.5 rounded-full border font-semibold">Batal</button>
              <button onClick={create} data-testid="new-page-create" className="px-6 py-2.5 rounded-full bg-maroon-800 text-white font-semibold">Buat & Edit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPages;
